import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Popover,
  Radio,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { primaryTint } from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import { useAgentEdit } from 'components/@home/@settings/Tabs/Agents/AgentEditContext';
import AgentAvatar from 'components/@home/@settings/Tabs/Agents/AgentAvatar';
import AgentColorPicker from 'components/@home/@settings/Tabs/Agents/AgentColorPicker';
import ExportAgentModal from 'components/@home/@settings/Tabs/Agents/ExportAgentModal';
import ImportAgentModal from 'components/@home/@settings/Tabs/Agents/ImportAgentModal';
import InstructionSaveFooter from 'components/@home/@settings/Tabs/Agents/InstructionSaveFooter';
import { getAgentColorStyle } from 'components/@home/@settings/Tabs/Agents/agentColorPalette';
import IdentityCard from 'components/@home/@settings/Tabs/Agents/IdentityCard';
import ConversationTimeLimitCard from 'components/@home/@settings/Tabs/Agents/panels/ConversationTimeLimitCard';
import GreetingCard from 'components/@home/@settings/Tabs/Agents/panels/GreetingCard';
import AgentLanguagesCard from 'components/@home/@settings/Tabs/Agents/panels/AgentLanguagesCard';
import AgentModelCard from 'components/@home/@settings/Tabs/Agents/panels/AgentModelCard';
import InactivityHandlingCard from 'components/@home/@settings/Tabs/Agents/panels/InactivityHandlingCard';
import { normalizeTimeoutSettings } from 'components/@home/@settings/Tabs/Agents/panels/timeoutDefaults';
import { ChatFlowContext } from 'components/@home/@settings/Tabs/ChatFlowContext';
import connector from 'components/@home/@settings/Tabs/connector';
import TimeZone from 'components/controls/TimeZone';
import AccountOutlineIcon from 'mdi-react/AccountOutlineIcon';
import TuneVerticalIcon from 'mdi-react/TuneVerticalIcon';
import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import GenderFemaleIcon from 'mdi-react/GenderFemaleIcon';
import GenderMaleIcon from 'mdi-react/GenderMaleIcon';
import GenderMaleFemaleIcon from 'mdi-react/GenderMaleFemaleIcon';
import InformationOutlineIcon from 'mdi-react/InformationOutlineIcon';
import TrashCanOutlineIcon from 'mdi-react/TrashCanOutlineIcon';
import TrayArrowDownIcon from 'mdi-react/TrayArrowDownIcon';
import TrayArrowUpIcon from 'mdi-react/TrayArrowUpIcon';
import { bool, func, node, object } from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { buildAgenticPath } from 'utils/agenticRoute';
import { I18n } from 'utils/i18n';

const TAB_GENERAL = 'general';
const TAB_CONFIGURATION = 'configuration';

const buildOverviewDraft = agent => ({
  name: agent?.name || '',
  description: agent?.description || '',
  gender: agent?.gender || 'neutral',
  timezone: agent?.timezone || '',
  skillsBehavior: agent?.skillsBehavior || 'dynamic',
  skillRoutingMode: agent?.skillRoutingMode || 'automated',
  attachedTools: Array.isArray(agent?.attachedTools) ? agent.attachedTools : [],
  initialMessages: {
    chat: agent?.initialMessages?.chat || '',
    voice: agent?.initialMessages?.voice || '',
  },
  disableInitialMessage: Boolean(agent?.disableInitialMessage),
  timeoutSettings: normalizeTimeoutSettings(agent?.timeoutSettings),
  locale: agent?.locale || 'es',
  supportedLocales: Array.isArray(agent?.supportedLocales)
    ? agent.supportedLocales
    : [agent?.locale || 'es'],
  autoSwitchLanguage: agent?.autoSwitchLanguage !== false,
  model: agent?.model || {},
});

const serializeOverviewDraft = draft => JSON.stringify(draft);

const cardSx = {
  width: '100%',
  border: 1,
  borderColor: 'divider',
  borderRadius: '12px',
  p: 2.5,
  bgcolor: 'background.paper',
};

const cardTitleSx = {
  fontSize: theme => theme.typography.pxToRem(16),
  fontWeight: 700,
  color: 'text.primary',
  mb: 2,
};

const cardDividerSx = {
  height: '1px',
  bgcolor: 'divider',
  mb: 2.5,
};

const fieldLabelSx = {
  fontSize: theme => theme.typography.pxToRem(12.5),
  color: 'text.secondary',
  mb: 0.5,
};

const gridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
  gap: 2.5,
};

const genderOptionSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

const Field = ({ label = null, children = null }) => (
  <Box>
    <Typography sx={fieldLabelSx}>{label}</Typography>
    {children}
  </Box>
);

Field.propTypes = { label: node, children: node };

const GENDER_LABEL_KEY = {
  female: 'GenderFemale',
  male: 'GenderMale',
  neutral: 'GenderNeutral',
};

const renderGenderIcon = (value, theme) => {
  if (value === 'female') {
    return (
      <Box component="span" sx={{ color: 'secondary.main', display: 'inline-flex' }}>
        <GenderFemaleIcon size={16} />
      </Box>
    );
  }
  if (value === 'male') {
    return (
      <Box
        component="span"
        sx={{
          color: theme.palette.info?.main || theme.palette.primary.main,
          display: 'inline-flex',
        }}
      >
        <GenderMaleIcon size={16} />
      </Box>
    );
  }
  return (
    <Box component="span" sx={{ color: 'text.secondary', display: 'inline-flex' }}>
      <GenderMaleFemaleIcon size={16} />
    </Box>
  );
};

const OverviewPanel = ({
  actions,
  agent = null,
  flow = null,
  onSaveDraft,
  onSaveFlowColor = null,
  showColorPicker = true,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { authCompany } = useAgentEdit();
  const { setToDelete } = useContext(ChatFlowContext);
  const [colorAnchor, setColorAnchor] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [draft, setDraft] = useState(() => buildOverviewDraft(agent));
  const [savedBaseline, setSavedBaseline] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [settingsTab, setSettingsTab] = useState(TAB_GENERAL);
  const saveFeedbackTimeoutRef = useRef(null);

  const clearSaveFeedbackLater = useCallback(() => {
    if (saveFeedbackTimeoutRef.current) {
      clearTimeout(saveFeedbackTimeoutRef.current);
    }
    saveFeedbackTimeoutRef.current = setTimeout(() => {
      setSaveFeedback(null);
      saveFeedbackTimeoutRef.current = null;
    }, 3000);
  }, []);

  useEffect(
    () => () => {
      if (saveFeedbackTimeoutRef.current) {
        clearTimeout(saveFeedbackTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!agent?._id) return;
    const next = buildOverviewDraft(agent);
    setDraft(next);
    setSavedBaseline(serializeOverviewDraft(next));
    setSaveFeedback(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset draft only when agent identity changes
  }, [agent?._id]);

  const dirty = useMemo(() => {
    if (savedBaseline == null) return false;
    return serializeOverviewDraft(draft) !== savedBaseline;
  }, [draft, savedBaseline]);

  useEffect(() => {
    if (dirty && saveFeedback?.severity === 'success') {
      setSaveFeedback(null);
    }
  }, [dirty, saveFeedback?.severity]);

  const patchDraft = (key, value) => {
    setDraft(prev => ({ ...prev, [key]: value }));
  };

  const patchDraftFields = fields => {
    setDraft(prev => ({ ...prev, ...fields }));
  };

  const handleSave = async () => {
    if (!dirty) return;
    setSaving(true);
    setSaveFeedback(null);
    try {
      await onSaveDraft(draft);
      const serialized = serializeOverviewDraft(draft);
      setSavedBaseline(serialized);
      setSaveFeedback({
        severity: 'success',
        message: I18n.t('AgenticChatFlow.ToolSaved'),
      });
      clearSaveFeedbackLater();
    } catch {
      setSaveFeedback({
        severity: 'error',
        message: I18n.t('AgenticChatFlow.ToolSaveError'),
      });
      clearSaveFeedbackLater();
    } finally {
      setSaving(false);
    }
  };

  const openExportAgent = () => {
    setMenuAnchor(null);
    setExportOpen(true);
  };

  const openImportAgent = () => {
    setMenuAnchor(null);
    setImportOpen(true);
  };

  const handleImported = async result => {
    await actions.chatFlows.load();
    const newFlowId = result?.chatFlowId;
    if (newFlowId && authCompany) {
      navigate(
        buildAgenticPath(authCompany, {
          flowId: newFlowId,
          section: 'overview',
        }),
      );
    }
  };

  const handleDelete = () => {
    setMenuAnchor(null);
    if (flow) setToDelete?.(flow);
  };

  const { bg } = getAgentColorStyle(flow, theme.palette.mode);
  const gender = draft.gender;
  const timezone = draft.timezone;
  const behavior = draft.skillsBehavior;
  const routing = draft.skillRoutingMode;

  const behaviorCards = [
    {
      id: 'static',
      title: I18n.t('AgenticChatFlow.BehaviorStaticTitle'),
      helper: I18n.t('AgenticChatFlow.BehaviorStaticHelper'),
    },
    {
      id: 'dynamic',
      title: I18n.t('AgenticChatFlow.BehaviorDynamicTitle'),
      helper: I18n.t('AgenticChatFlow.BehaviorDynamicHelper'),
    },
  ];

  const behaviorCardSx = {
    border: 1,
    borderColor: 'divider',
    borderRadius: '12px',
    p: 2,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
    transition: 'border-color 120ms ease, background-color 120ms ease',
    '&:hover': {
      borderColor: 'text.disabled',
    },
  };

  const behaviorCardSelectedSx = {
    borderColor: 'primary.main',
    bgcolor: primaryTint(theme, 0.06),
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <Box sx={{ flexShrink: 0, pt: 2.5, px: 2.5 }}>
        {agent && (
          <IdentityCard
            icon={<AgentAvatar flow={flow} size={48} />}
            bareIcon
            title={draft.name}
            description={draft.description}
            actions={
              <>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={e => setMenuAnchor(e.currentTarget)}
                  size="small"
                  sx={{ borderColor: 'divider', minWidth: 0 }}
                  aria-label={I18n.t('AgenticChatFlow.MoreActions')}
                >
                  <DotsHorizontalIcon size={20} />
                </Button>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={() => setMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem onClick={openImportAgent}>
                    <TrayArrowUpIcon size={16} style={{ marginRight: 8 }} />
                    {I18n.t('AgenticChatFlow.ImportAgent')}
                  </MenuItem>
                  <MenuItem onClick={openExportAgent}>
                    <TrayArrowDownIcon size={16} style={{ marginRight: 8 }} />
                    {I18n.t('AgenticChatFlow.ExportAgent')}
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                    <TrashCanOutlineIcon size={16} style={{ marginRight: 8 }} />
                    {I18n.t('AgenticChatFlow.DeleteAgent')}
                  </MenuItem>
                </Menu>
                <ExportAgentModal
                  open={exportOpen}
                  agentId={agent?._id}
                  agentName={agent?.name || flow?.name}
                  onClose={() => setExportOpen(false)}
                />
                <ImportAgentModal
                  open={importOpen}
                  onClose={() => setImportOpen(false)}
                  onImported={handleImported}
                />
              </>
            }
          />
        )}
        {agent && (
          <Tabs
            value={settingsTab}
            onChange={(_, v) => setSettingsTab(v)}
            sx={{
              minHeight: 44,
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTabs-flexContainer': { gap: 2 },
            }}
            TabIndicatorProps={{ sx: { height: 2 } }}
          >
            <Tab
              sx={{
                textTransform: 'none',
                minHeight: 44,
                minWidth: 'auto',
                py: 1,
                px: 0.5,
                fontSize: t => t.typography.pxToRem(13),
                fontWeight: 500,
              }}
              value={TAB_GENERAL}
              icon={<AccountOutlineIcon size={16} />}
              iconPosition="start"
              label={I18n.t('AgenticChatFlow.NavGeneral')}
            />
            <Tab
              sx={{
                textTransform: 'none',
                minHeight: 44,
                minWidth: 'auto',
                py: 1,
                px: 0.5,
                fontSize: t => t.typography.pxToRem(13),
                fontWeight: 500,
              }}
              value={TAB_CONFIGURATION}
              icon={<TuneVerticalIcon size={16} />}
              iconPosition="start"
              label={I18n.t('AgenticChatFlow.TabConfiguration')}
            />
          </Tabs>
        )}
      </Box>
      <Box sx={{ flex: '1 1 0', minHeight: 0, overflowY: 'auto', p: 2.5 }}>
        <Stack spacing={3} sx={{ width: '100%' }}>
          {settingsTab === TAB_GENERAL && (
            <>
              <Box sx={cardSx}>
                <Typography sx={cardTitleSx}>{I18n.t('AgenticChatFlow.DetailsTitle')}</Typography>
                <Box sx={cardDividerSx} />
                <Box sx={gridSx}>
                  <Field label={I18n.t('AgenticChatFlow.Name')}>
                    <TextField
                      value={draft.name}
                      onChange={e => patchDraft('name', e.target.value)}
                      fullWidth
                      size="small"
                      slotProps={
                        showColorPicker && flow && onSaveFlowColor
                          ? {
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      size="small"
                                      sx={{ p: '2px' }}
                                      onClick={e => setColorAnchor(e.currentTarget)}
                                      aria-label={I18n.t('AgenticChatFlow.PickColor')}
                                    >
                                      <Box
                                        sx={{
                                          width: 20,
                                          height: 20,
                                          borderRadius: '5px',
                                          border: 1,
                                          borderColor: 'divider',
                                          backgroundColor: bg,
                                        }}
                                      />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              },
                            }
                          : undefined
                      }
                    />
                  </Field>
                  <Field label={I18n.t('AgenticChatFlow.Title')}>
                    <TextField
                      value={draft.description}
                      onChange={e => patchDraft('description', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Field>
                  <Field label={I18n.t('AgenticChatFlow.Gender')}>
                    <Select
                      value={gender}
                      onChange={e => patchDraft('gender', e.target.value)}
                      fullWidth
                      size="small"
                      renderValue={value => (
                        <Box sx={genderOptionSx}>
                          {renderGenderIcon(value, theme)}
                          {I18n.t(`AgenticChatFlow.${GENDER_LABEL_KEY[value] || 'GenderNeutral'}`)}
                        </Box>
                      )}
                    >
                      <MenuItem value="female" sx={genderOptionSx}>
                        {renderGenderIcon('female', theme)}
                        {I18n.t('AgenticChatFlow.GenderFemale')}
                      </MenuItem>
                      <MenuItem value="male" sx={genderOptionSx}>
                        {renderGenderIcon('male', theme)}
                        {I18n.t('AgenticChatFlow.GenderMale')}
                      </MenuItem>
                      <MenuItem value="neutral" sx={genderOptionSx}>
                        {renderGenderIcon('neutral', theme)}
                        {I18n.t('AgenticChatFlow.GenderNeutral')}
                      </MenuItem>
                    </Select>
                  </Field>
                  <Field label={I18n.t('AgenticChatFlow.Timezone')}>
                    <TimeZone
                      value={timezone}
                      onChange={selected => patchDraft('timezone', selected?.value || '')}
                    />
                  </Field>
                </Box>
              </Box>

              <Box sx={cardSx}>
                <AgentLanguagesCard draft={draft} onChange={patchDraftFields} />
              </Box>

              <Box sx={cardSx}>
                <Typography sx={cardTitleSx}>
                  {I18n.t('AgenticChatFlow.SkillsBehaviorTitle')}
                </Typography>
                <Box sx={cardDividerSx} />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 2,
                    mb: 2.5,
                  }}
                >
                  {behaviorCards.map(item => {
                    const selected = behavior === item.id;
                    return (
                      <Box
                        key={item.id}
                        sx={[behaviorCardSx, selected && behaviorCardSelectedSx]}
                        onClick={() => patchDraft('skillsBehavior', item.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            patchDraft('skillsBehavior', item.id);
                          }
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: t => t.typography.pxToRem(14),
                              fontWeight: 600,
                              color: 'text.primary',
                            }}
                          >
                            {item.title}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: t => t.typography.pxToRem(12.5),
                              color: 'text.secondary',
                              mt: 0.25,
                            }}
                          >
                            {item.helper}
                          </Typography>
                        </Box>
                        <Radio
                          checked={selected}
                          onChange={() => patchDraft('skillsBehavior', item.id)}
                          size="small"
                          sx={{ p: 0.5 }}
                        />
                      </Box>
                    );
                  })}
                </Box>
                {behavior === 'dynamic' && (
                  <>
                    <Field label={I18n.t('AgenticChatFlow.SkillRoutingMode')}>
                      <Select
                        value={routing}
                        onChange={e => patchDraft('skillRoutingMode', e.target.value)}
                        fullWidth
                        size="small"
                      >
                        <MenuItem value="automated">
                          {I18n.t('AgenticChatFlow.RoutingAutomated')}
                        </MenuItem>
                        <MenuItem value="custom">
                          {I18n.t('AgenticChatFlow.RoutingCustom')}
                        </MenuItem>
                      </Select>
                    </Field>
                    <Alert
                      icon={<InformationOutlineIcon size={18} />}
                      severity="info"
                      sx={{
                        mt: 1.5,
                        borderRadius: '10px',
                        bgcolor: 'background.default',
                        border: 1,
                        borderColor: 'divider',
                        '& .MuiAlert-icon': { color: 'text.secondary' },
                      }}
                    >
                      {I18n.t('AgenticChatFlow.RoutingHelp')}
                    </Alert>
                  </>
                )}
              </Box>
            </>
          )}
          {settingsTab === TAB_CONFIGURATION && agent && (
            <Stack spacing={4}>
              <Box sx={cardSx}>
                <AgentModelCard draft={draft} onPatch={patchDraft} />
              </Box>
              <GreetingCard draft={draft} onPatch={patchDraft} />
              <ConversationTimeLimitCard draft={draft} onPatch={patchDraft} />
              <InactivityHandlingCard draft={draft} onPatch={patchDraft} />
            </Stack>
          )}

          <Popover
            open={Boolean(colorAnchor)}
            anchorEl={colorAnchor}
            onClose={() => setColorAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ p: 1.5, minWidth: 260 }}>
              <AgentColorPicker
                flow={flow}
                color={flow?.color}
                onChange={value => {
                  onSaveFlowColor(value);
                }}
                nameSeed={flow?.name}
              />
            </Box>
          </Popover>
        </Stack>
      </Box>
      {agent && (
        <InstructionSaveFooter
          dirty={dirty}
          saving={saving}
          saveFeedback={saveFeedback}
          onSave={handleSave}
        />
      )}
    </Box>
  );
};

OverviewPanel.propTypes = {
  actions: object.isRequired,
  agent: object,
  flow: object,
  onSaveDraft: func.isRequired,
  onSaveFlowColor: func,
  showColorPicker: bool,
};

export default connector(OverviewPanel);
